import { useEffect, useState } from "react";
import BossDialog from "../../../components/BossDialog";
import * as API from "../../../framework/API/api";
import { ENTITYNAME, TOAST_TYPES } from "../../../framework/constant/constant";
import { EditPageHeader } from "../../../components/EditPageHeader";
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { getSelectedState, Grid, GridColumn } from '@progress/kendo-react-grid';
import { getter } from '@progress/kendo-data-query';
import { TimeRangePicker } from '../../../framework/forms/helpercomponents/timepicker/TimeRangePicker';
import MyMultiSelect from '../../../components/MyMultiSelect';
import NodeTreeViewForm from './NodeTreeViewForm';
import ToastNotification from '../../../components/ToastNotification';
import { useTranslation } from '../../../locale/useTranslation';
import { utility } from '../../../framework/utility/utilityProvider';
import moment from "moment";
import { DateTimeCell } from "../../../framework/forms/helpercomponents/CustomGridCells/DateCell";
import { Loader } from "../../../framework/forms/helpercomponents/Loader";

const SELECTED_FIELD = "selected";
const DATA_ITEM_KEY = "_id";
const idGetter = getter(DATA_ITEM_KEY);

const CreateSeriesTransmissionForm = (props) => {

    const { onClose, item = {} } = props;

    const lang = useTranslation();

    const getInitialFromEpisode = () => {
        const epNum = item?.MediaEpisode?.EpisodeNumber ?? item?.mediaEpisode?.EpisodeNumber ?? item?.EpisodeNumber;
        return epNum !== undefined && epNum !== null && epNum !== "" ? Number(epNum) : "";
    };

    const [isLoading, setIsLoading] = useState(false);
    const [isSaveLoading, setIsSaveLoading] = useState(false);
    const [gridData, setGridData] = useState([]);
    const [selectedState, setSelectedState] = useState({});
    const [bulkStartDate, setBulkStartDate] = useState("");
    const [bulkStartTime, setBulkStartTime] = useState("00:00");
    const [bulkEndDate, setBulkEndDate] = useState("");
    const [bulkEndTime, setBulkEndTime] = useState("00:00");
    const [updateStartDate, setUpdateStartDate] = useState(false);
    const [updateEndDate, setUpdateEndDate] = useState(false);
    const [useStartStack, setUseStartStack] = useState(false);
    const [startStackOffset, setStartStackOffset] = useState(0);
    const [useEndStack, setUseEndStack] = useState(false);
    const [endStackOffset, setEndStackOffset] = useState(0);
    const [episodeRange, setEpisodeRange] = useState({
        from: getInitialFromEpisode(),
        to: ""
    });

    const [catalogues, setCatalogues] = useState([]);
    const [parentNodes, setParentNodes] = useState([]);
    const [ottPlatforms, setOttPlatforms] = useState([]);

    const [dataItem, setDataItem] = useState({
        Catalogue: {},
        ParentNodes: [],
        Nodes: [],
        Ott_Platforms: [],
        TransmissionType: {},
        AutoCreateNode: false
    });
    const [showNodePopup, setShowNodePopup] = useState(false);

    useEffect(() => {
        loadCombo();
        loadData();
    }, []);

    useEffect(() => {
        const selectedEpisodes = gridData.filter(x => selectedState[idGetter(x)]);
        if (selectedEpisodes.length === 1) {
            const ep = selectedEpisodes[0];
            const startDate = ep.Publishing?.PublishStartDate;
            const endDate = ep.Publishing?.PublishEndDate;
            setBulkStartDate(startDate ? moment.utc(startDate).format('YYYY-MM-DD') : "");
            setBulkStartTime(startDate ? moment.utc(startDate).format('HH:mm') : "00:00");
            setBulkEndDate(endDate ? moment.utc(endDate).format('YYYY-MM-DD') : "");
            setBulkEndTime(endDate ? moment.utc(endDate).format('HH:mm') : "00:00");
            setUpdateStartDate(!!startDate);
            setUpdateEndDate(!!endDate);
        } else {
            setBulkStartDate("");
            setBulkStartTime("00:00");
            setBulkEndDate("");
            setBulkEndTime("00:00");
            setUpdateStartDate(false);
            setUpdateEndDate(false);
        }
    }, [selectedState, gridData]);

    const loadCombo = async () => {
        try {
            let catalogueRes = await API.getDataLookup(ENTITYNAME.Catalogues, { query: ["Archive", "!=", true] });
            setCatalogues(catalogueRes?.data ?? []);
            let ottPlatformRes = await API.getDataLookup(ENTITYNAME.Platform, { query: ["Archive", "!=", true] });
            setOttPlatforms(ottPlatformRes?.data ?? []);

            let parentNodeRes = await API.getDataLookup(ENTITYNAME.Nodes, { query: ["Archive", "!=", true] });
            parentNodeRes = parentNodeRes?.data ?? [];
            let root = parentNodeRes?.filter((n) => !n.ParentNode);
            const vodMap = new Map();
            const searchChild = (parent) => {
                parentNodeRes?.forEach(n => {
                    if (n?.ParentNode === parent._id) {
                        vodMap.set(parent._id, parent);
                        searchChild(n);
                    }
                });
            }
            root?.forEach(n => {
                searchChild(n);
            })
            const vodChannelsArray = Array.from(vodMap.values());
            setParentNodes(vodChannelsArray);

            let itemProviders = item?.MediaEpisode?.Provider?._id ?? item?.mediaEpisode?.Provider?._id ?? null;
            let pNodes = itemProviders ? parentNodeRes.filter(n => n?.Providers?.includes(itemProviders)) : [];
            let catalogue = (itemProviders && pNodes.length > 0) ? pNodes[0].Catalogue : catalogueRes?.data?.[0] ?? {};
            let transmissionTypeRes = await API.getDataLookup(ENTITYNAME.TransmissionTypes, { sort: { SID: 1 } });

            setDataItem({
                Catalogue: catalogue,
                Ott_Platforms: item?.SegmentExtension?.platforms ?? utility.getDefaultItems(ottPlatformRes?.data),
                TransmissionType: transmissionTypeRes?.data?.length > 0 ? transmissionTypeRes?.data?.find(x => x?.Name == "SVOD") : transmissionTypeRes?.data?.[0] ?? {},
                ParentNodes: pNodes,
                Nodes: [],
                AutoCreateNode: false,
            });
        } catch (error) {
            console.error("Error loading combo data:", error);
        }
    };

    const loadData = async () => {
        setIsLoading(true);
        try {
            const seasonId = item?.MediaEpisode?.Season_id ?? item?.mediaEpisode?.Season_id;
            if (seasonId) {
                let res = await API.getVersionFromSeries({
                    seasonId: seasonId
                });
                const data = res.data ?? [];
                setGridData(data);
                if (data.length > 0) {
                    const epNums = data.map(x => parseInt(x.EpisodeNumber)).filter(x => !isNaN(x));
                    if (epNums.length > 0) {
                        const maxEp = Math.max(...epNums);
                        setEpisodeRange(prev => ({ ...prev, to: maxEp }));
                    }
                }
            } else {
                setGridData([]);
            }
        } catch (error) {
            console.error("Error loading episode data:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const onChange = (e) => {
        if (e.target.name === 'AutoCreateNode') {
            setDataItem(prev => ({ ...prev, AutoCreateNode: !prev.AutoCreateNode }));
        } else {
            setDataItem(prev => ({ ...prev, [e.target.name]: e.target.value }));
        }
    };

    const [lastSelectedIndex, setLastSelectedIndex] = useState(null);

    const onSelectionChange = (event) => {
        const syntheticEvent = event.syntheticEvent;
        const isShiftKey = syntheticEvent && syntheticEvent.shiftKey;
        const clickedItem = event.dataItem;
        const clickedIndex = filteredGridData.findIndex(x => idGetter(x) === idGetter(clickedItem));

        if (isShiftKey && lastSelectedIndex !== null && clickedIndex !== -1) {
            const start = Math.min(lastSelectedIndex, clickedIndex);
            const end = Math.max(lastSelectedIndex, clickedIndex);
            const rangeItems = filteredGridData.slice(start, end + 1);

            const targetSelected = !selectedState[idGetter(clickedItem)];

            const newSelectedState = { ...selectedState };
            rangeItems.forEach((item) => {
                if (targetSelected) {
                    newSelectedState[idGetter(item)] = true;
                } else {
                    delete newSelectedState[idGetter(item)];
                }
            });
            setSelectedState(newSelectedState);
        } else {
            const newSelectedState = getSelectedState({
                event,
                selectedState: selectedState,
                dataItemKey: DATA_ITEM_KEY,
            });
            setSelectedState(newSelectedState);
        }

        if (clickedIndex !== -1) {
            setLastSelectedIndex(clickedIndex);
        }
    };

    const onHeaderSelectionChange = (event) => {
        const checkboxElement = event.syntheticEvent.target;
        const checked = checkboxElement.checked;
        const newSelectedState = { ...selectedState };
        event.dataItems.forEach((item) => {
            if (checked) {
                newSelectedState[idGetter(item)] = true;
            } else {
                delete newSelectedState[idGetter(item)];
            }
        });
        setSelectedState(newSelectedState);
    };

    const handleSelectRange = () => {
        const from = episodeRange.from !== "" ? parseInt(episodeRange.from) : null;
        const to = episodeRange.to !== "" ? parseInt(episodeRange.to) : null;

        if (from === null && to === null) {
            const newSelectedState = { ...selectedState };
            filteredGridData.forEach((item) => {
                newSelectedState[idGetter(item)] = true;
            });
            setSelectedState(newSelectedState);
            ToastNotification({
                type: TOAST_TYPES.SUCCESS,
                title: lang.success_message_title,
                message: `Selected ${filteredGridData.length} item(s).`
            });
            return;
        }

        const itemsToSelect = gridData.filter((item) => {
            const epNum = parseInt(item?.EpisodeNumber || item?.mediaEpisode?.EpisodeNumber);
            if (isNaN(epNum)) return false;
            if (from !== null && epNum < from) return false;
            if (to !== null && epNum > to) return false;
            return true;
        });

        if (itemsToSelect.length === 0) {
            ToastNotification({
                type: TOAST_TYPES.WARNING,
                title: lang.warning_message_title,
                message: "No episodes found within the specified range."
            });
            return;
        }

        const newSelectedState = { ...selectedState };
        itemsToSelect.forEach((item) => {
            newSelectedState[idGetter(item)] = true;
        });

        setSelectedState(newSelectedState);
        ToastNotification({
            type: TOAST_TYPES.SUCCESS,
            title: lang.success_message_title,
            message: `Selected ${itemsToSelect.length} episode(s) in range.`
        });
    };

    const handleDeselectRange = () => {
        const from = episodeRange.from !== "" ? parseInt(episodeRange.from) : null;
        const to = episodeRange.to !== "" ? parseInt(episodeRange.to) : null;

        const itemsToDeselect = gridData.filter((item) => {
            const epNum = parseInt(item?.EpisodeNumber || item?.mediaEpisode?.EpisodeNumber);
            if (isNaN(epNum)) return false;
            if (from !== null && epNum < from) return false;
            if (to !== null && epNum > to) return false;
            return true;
        });

        const newSelectedState = { ...selectedState };
        itemsToDeselect.forEach((item) => {
            delete newSelectedState[idGetter(item)];
        });

        setSelectedState(newSelectedState);
    };

    const handleBulkUpdateDates = () => {
        if (!updateStartDate && !updateEndDate) {
            ToastNotification({
                type: TOAST_TYPES.WARNING,
                title: lang.warning_message_title,
                message: lang.please_select_at_least_one_field_to_update_error_message
            });
            return;
        }

        if (updateStartDate && (!bulkStartDate || !bulkStartTime || bulkStartTime.includes('_'))) {
            ToastNotification({
                type: TOAST_TYPES.WARNING,
                title: lang.warning_message_title,
                message: lang.please_select_start_date_error_message
            });
            return;
        }

        if (updateEndDate && (!bulkEndDate || !bulkEndTime || bulkEndTime.includes('_'))) {
            ToastNotification({
                type: TOAST_TYPES.WARNING,
                title: lang.warning_message_title,
                message: lang.please_select_end_date_error_message
            });
            return;
        }

        const selectedEpisodes = gridData.filter(x => selectedState[idGetter(x)]);
        if (selectedEpisodes.length === 0) {
            ToastNotification({
                type: TOAST_TYPES.WARNING,
                title: lang.warning_message_title,
                message: lang.no_episode_selected_error_message
            });
            return;
        }

        let hasDateError = false;
        const updatedGridData = gridData.map(item => {
            if (selectedState[idGetter(item)]) {
                const selectedIndex = selectedEpisodes.findIndex(x => idGetter(x) === idGetter(item));

                let newStartDate = item.Publishing?.PublishStartDate;
                let newEndDate = item.Publishing?.PublishEndDate;

                if (updateStartDate) {
                    const startOffsetDays = useStartStack ? selectedIndex * startStackOffset : 0;
                    newStartDate = moment.utc(`${bulkStartDate}T${bulkStartTime}`).add(startOffsetDays, 'days').valueOf();
                }
                if (updateEndDate) {
                    const endOffsetDays = useEndStack ? selectedIndex * endStackOffset : 0;
                    newEndDate = moment.utc(`${bulkEndDate}T${bulkEndTime}`).add(endOffsetDays, 'days').valueOf();
                }

                if (newStartDate && newEndDate && newStartDate > newEndDate) {
                    hasDateError = true;
                }

                return {
                    ...item,
                    Publishing: {
                        ...(item.Publishing || {}),
                        PublishStartDate: newStartDate,
                        PublishEndDate: newEndDate
                    }
                };
            }
            return item;
        });

        if (hasDateError) {
            ToastNotification({
                type: TOAST_TYPES.WARNING,
                title: lang.warning_message_title,
                message: lang.start_date_cannot_be_greater_than_end_date_error_message
            });
            return;
        }

        setGridData(updatedGridData);
        setUseStartStack(false);
        setStartStackOffset(0);
        setUseEndStack(false);
        setEndStackOffset(0);

        ToastNotification({
            type: TOAST_TYPES.SUCCESS,
            title: lang.success_message_title,
            message: `Updated dates for ${selectedEpisodes.length} selected episode(s).`
        });
    };

    const handleSelectedNodes = (nodes) => {
        setDataItem(prev => {
            const merged = [...(prev.Nodes || []), ...nodes];
            const unique = Array.from(
                new Map(merged.map(item => [item._id, item])).values()
            );
            return {
                ...prev,
                Nodes: unique
            };
        });
        setShowNodePopup(false);
    };

    const isValid = () => {
        const selectedEpisodes = gridData.filter(x => selectedState[idGetter(x)]);
        if (selectedEpisodes.length === 0) {
            ToastNotification({
                type: TOAST_TYPES.ERROR,
                title: lang.invalid_error_message_title,
                message: lang.please_select_atleast_one_item
            });
            return false;
        }

        // Validate dates for all selected episodes
        for (const episode of selectedEpisodes) {
            const startDate = episode.Publishing?.PublishStartDate ?? episode.PublishStartDate;
            const endDate = episode.Publishing?.PublishEndDate ?? episode.PublishEndDate;
            if (startDate && endDate && startDate > endDate) {
                ToastNotification({
                    type: TOAST_TYPES.ERROR,
                    title: lang.invalid_error_message_title,
                    message: `For Episode ${episode.EpisodeNumber || ""}: Start Date cannot be greater than End Date.`
                });
                return false;
            }
        }
        if (!dataItem.Catalogue || Object.keys(dataItem.Catalogue).length === 0) {
            ToastNotification({ type: TOAST_TYPES.ERROR, title: lang.invalid_error_message_title, message: lang.please_select_catalogue_error_message });
            return false;
        }
        if (dataItem.ParentNodes.length === 0) {
            ToastNotification({ type: TOAST_TYPES.ERROR, title: lang.invalid_error_message_title, message: lang.please_select_vod_channels_error_message });
            return false;
        }
        if (!dataItem.AutoCreateNode && dataItem.Nodes?.length === 0) {
            ToastNotification({ type: TOAST_TYPES.ERROR, title: lang.invalid_error_message_title, message: lang.please_select_nodes_error_message });
            return false;
        }
        if (dataItem.Ott_Platforms.length === 0) {
            ToastNotification({ type: TOAST_TYPES.ERROR, title: lang.invalid_error_message_title, message: lang.please_select_ott_platform });
            return false;
        }
        return true;
    };

    const handleSubmit = async () => {
        if (!isValid()) return;
        setIsSaveLoading(true);
        try {
            const selectedEpisodes = gridData.filter(x => selectedState[idGetter(x)]);
            const payload = {
                mediaEpisodes: selectedEpisodes.map((x) => {
                    return {
                        _id: x._id?.toString(),
                        SID: x.SID,
                        MediaCategory: x.MediaCategory,
                        PublishStartDate: x.Publishing?.PublishStartDate,
                        PublishEndDate: x.Publishing?.PublishEndDate,
                        Segment: x?.Segments?.[0] ?? {},
                    };
                }),
                transmissionDetails: {
                    Catalogue: { _id: dataItem.Catalogue._id, Description: dataItem.Catalogue?.Description },
                    ParentNodes: dataItem.ParentNodes,
                    Nodes: dataItem.Nodes,
                    Ott_Platforms: dataItem.Ott_Platforms,
                    TransmissionType: dataItem.TransmissionType,
                    AutoCreateNode: dataItem.AutoCreateNode ?? false
                }
            };

            let res = await API.createSeriesTransmission(payload);
            if (res.success) {
                ToastNotification({ type: TOAST_TYPES.SUCCESS, title: lang.success_message_title, message: res.message });
                onClose();
            } else {
                ToastNotification({ type: TOAST_TYPES.ERROR, title: lang.invalid_error_message_title, message: res.message });
            }
        } catch (error) {
            console.error("Error submitting series transmission:", error);
            ToastNotification({ type: TOAST_TYPES.ERROR, title: lang.invalid_error_message_title, message: error.message });
        } finally {
            setIsSaveLoading(false);
        }
    };

    const epNums = gridData.map(x => parseInt(x.EpisodeNumber)).filter(x => !isNaN(x));
    const minEp = epNums.length > 0 ? Math.min(...epNums) : null;
    const maxEp = epNums.length > 0 ? Math.max(...epNums) : null;
    const baseTitle = item?.MediaEpisode?.Title ?? item?.mediaEpisode?.Title ?? item?.Title ?? item?.WorkingTitle ?? "";
    const formTitle = `${baseTitle}${minEp !== null && maxEp !== null ? ` (Episodes: ${minEp} to ${maxEp})` : ""}`;

    const filteredGridData = gridData.filter((item) => {
        const epNum = parseInt(item.EpisodeNumber);
        if (isNaN(epNum)) return true;
        const from = episodeRange.from !== "" ? parseInt(episodeRange.from) : null;
        const to = episodeRange.to !== "" ? parseInt(episodeRange.to) : null;
        if (from !== null && epNum < from) return false;
        if (to !== null && epNum > to) return false;
        return true;
    });

    const gridDataWithSelection = filteredGridData.map((item) => ({
        ...item,
        [SELECTED_FIELD]: selectedState[idGetter(item)],
    }));

    const selectedEpisodes = gridData.filter(x => selectedState[idGetter(x)]);

    return (
        <BossDialog
            width="80%"
            title={"Create Series Transmission" + (gridData.length > 0 ? ` (Total Episodes: ${gridData.length})` : "")}
            onClose={onClose}
        >
            <style>{`
                @keyframes slideInRight {
                    from {
                        transform: translateX(30px);
                        opacity: 0;
                    }
                    to {
                        transform: translateX(0);
                        opacity: 1;
                    }
                }
            `}</style>
            <div className="d-flex justify-content-between align-items-center">
                <EditPageHeader saveButtonTitle={"Create Transmission"} showTitle={false} onSubmit={handleSubmit} onCancel={onClose} isLoading={isSaveLoading} />
                {formTitle && (
                    <div style={{
                        backgroundColor: "#f8f9fa",
                        border: "1px solid #dee2e6",
                        borderRadius: "4px",
                        padding: "6px 12px",
                        display: "flex",
                        alignItems: "center",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
                        whiteSpace: "nowrap",
                        flexShrink: 0
                    }}>
                        <i className="fa fa-film" style={{ color: "#007bff", fontSize: "15px", marginRight: "8px" }} />
                        <span style={{ fontSize: "14px", fontWeight: "600", color: "#333" }}>
                            {formTitle}
                        </span>
                    </div>
                )}
            </div>
            <div style={{ position: 'relative', marginTop: '15px' }}>
                {/* Left side: Grid showing episode details */}
                <div
                    style={{
                        width: selectedEpisodes.length > 0 ? "calc(100% - 430px)" : "100%",
                        transition: 'width 0.2s ease',
                        paddingRight: selectedEpisodes.length > 0 ? "10px" : "0px"
                    }}
                >
                    <div style={{ marginBottom: "15px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <div style={{ display: "flex", alignItems: "center" }}>
                            <h6 style={{ fontWeight: "600", color: "#333", borderBottom: "2px solid #007bff", paddingBottom: "5px", display: "inline-block", margin: 0 }}>
                                {lang.episodes_label || "Episodes"}
                            </h6>
                            {selectedEpisodes.length === 0 && (
                                <span style={{ marginLeft: "15px", fontSize: "13px", color: "#6c757d", backgroundColor: "#f8f9fa", padding: "4px 10px", borderRadius: "4px", border: "1px solid #dee2e6" }}>
                                    <i className="fa fa-info-circle me-1" style={{ color: "#007bff" }}></i>
                                    {"Please select item show more details"}
                                </span>
                            )}
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <span style={{ fontSize: "13px", fontWeight: "600", color: "#495057" }}>
                                Episode Range:
                            </span>
                            <input
                                type="number"
                                className="form-control form-control-sm"
                                placeholder="From"
                                style={{ width: "70px", borderRadius: "4px" }}
                                value={episodeRange.from}
                                onChange={(e) => setEpisodeRange(prev => ({ ...prev, from: e.target.value === "" ? "" : Number(e.target.value) }))}
                            />
                            <span style={{ fontSize: "13px", color: "#495057" }}>to</span>
                            <input
                                type="number"
                                className="form-control form-control-sm"
                                placeholder="To"
                                style={{ width: "70px", borderRadius: "4px" }}
                                value={episodeRange.to}
                                onChange={(e) => setEpisodeRange(prev => ({ ...prev, to: e.target.value === "" ? "" : Number(e.target.value) }))}
                            />
                            <button
                                type="button"
                                className="k-button k-button-sm k-button-solid k-button-solid-primary"
                                onClick={handleSelectRange}
                                style={{ borderRadius: "4px", height: "30px", fontWeight: "600" }}
                                title="Select all episodes in range"
                            >
                                Select Range
                            </button>
                            <button
                                type="button"
                                className="k-button k-button-sm k-button-solid k-button-solid-base"
                                onClick={handleDeselectRange}
                                style={{ borderRadius: "4px", height: "30px", fontWeight: "600" }}
                                title="Deselect all episodes in range"
                            >
                                Deselect
                            </button>
                            <button
                                type="button"
                                className="k-button k-button-sm k-button-solid k-button-solid-base"
                                onClick={() => setEpisodeRange({ from: "", to: "" })}
                                style={{ borderRadius: "4px", height: "30px", fontWeight: "600" }}
                                title="Clear range filter"
                            >
                                Clear
                            </button>
                        </div>
                    </div>
                    <div>
                        {isLoading && <Loader />}
                        {!isLoading && (
                            <Grid
                                data={gridDataWithSelection}
                                style={{
                                    height: "calc(100vh - 230px)"
                                }}
                                resizable={true}
                                dataItemKey={DATA_ITEM_KEY}
                                selectedField={SELECTED_FIELD}
                                selectable={{
                                    enabled: true,
                                    drag: false,
                                    cell: false,
                                    mode: "multiple",
                                }}
                                onSelectionChange={onSelectionChange}
                                onHeaderSelectionChange={onHeaderSelectionChange}
                            >
                                <GridColumn
                                    field={SELECTED_FIELD}
                                    width="50px"
                                    headerSelectionValue={
                                        filteredGridData.length > 0 &&
                                        filteredGridData.findIndex(
                                            (item) => !selectedState[idGetter(item)]
                                        ) === -1
                                    }
                                    locked={true}
                                    filterable={false}
                                />
                                <GridColumn
                                    field="Title"
                                    title={lang.title_column}
                                />
                                <GridColumn
                                    field="WorkingTitle"
                                    title={lang.working_title_column}
                                />
                                <GridColumn
                                    field="EpisodeNumber"
                                    title={lang.episode_number_column}
                                    width="120px"
                                />
                                <GridColumn
                                    field="Publishing.PublishStartDate"
                                    title={lang.start_date_time_label}
                                    cell={DateTimeCell}
                                    width="180px"
                                />
                                <GridColumn
                                    field="Publishing.PublishEndDate"
                                    title={lang.end_date_time_label}
                                    cell={DateTimeCell}
                                    width="180px"
                                />
                            </Grid>
                        )}
                    </div>
                </div>

                {/* Right side: Form for transmission details */}
                {selectedEpisodes.length > 0 && (
                    <div
                        style={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            height: 'calc(100vh - 185px)',
                            width: '420px',
                            background: '#fff',
                            borderLeft: '1px solid #dee2e6',
                            boxShadow: '-8px 0 20px rgba(0, 0, 0, 0.08)',
                            display: 'flex',
                            flexDirection: 'column',
                            zIndex: 3,
                            animation: "slideInRight 0.2s ease-in-out forwards"
                        }}
                    >
                        <div style={{ padding: '12px 14px', borderBottom: '1px solid #e9ecef', backgroundColor: '#f8f9fa' }}>
                            <h6 className="mb-0" style={{ fontWeight: "600", color: "#333" }}>
                                {lang.transmission_details_label || "Transmission Details"}
                            </h6>
                        </div>
                        <div style={{ padding: '14px', overflowY: 'auto', flex: 1 }}>
                            <div className="row">
                                <div className="col-12 mt-2">
                                    <label style={{ fontWeight: "500", fontSize: "14px", color: "#495057", marginBottom: "4px" }}>
                                        {lang.catalogue_label} *
                                    </label>
                                    <DropDownList
                                        data={catalogues}
                                        name="Catalogue"
                                        dataItemKey="_id"
                                        textField="Description"
                                        value={dataItem.Catalogue}
                                        onChange={onChange}
                                        style={{ width: "100%" }}
                                    />
                                </div>
                                <div className="col-12 mt-2">
                                    <label style={{ fontWeight: "500", fontSize: "14px", color: "#495057", marginBottom: "4px" }}>
                                        {lang.vod_channels_label} *
                                    </label>
                                    <MyMultiSelect
                                        data={dataItem.Catalogue && dataItem.Catalogue._id ? parentNodes.filter((x) => x.Catalogue._id === dataItem.Catalogue._id) : []}
                                        name="ParentNodes"
                                        dataItemKey="_id"
                                        textField="Name"
                                        value={dataItem.ParentNodes}
                                        onChange={onChange}
                                        style={{ width: "100%" }}
                                    />
                                </div>
                                <div className="col-12 mt-2">
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                        <label style={{ fontWeight: "500", fontSize: "14px", color: "#495057", margin: 0 }}>
                                            {lang.nodes_label} {dataItem?.AutoCreateNode ? "" : "*"}
                                        </label>
                                        <div className="d-flex align-items-center">
                                            <input
                                                style={{ border: "1px solid grey", cursor: "pointer", width: "16px", height: "16px" }}
                                                name="AutoCreateNode"
                                                type="checkbox"
                                                id="auto-create-node-checkbox"
                                                checked={dataItem.AutoCreateNode}
                                                onChange={onChange}
                                            />
                                            <label htmlFor="auto-create-node-checkbox" style={{ marginLeft: "5px", cursor: "pointer", userSelect: "none", fontSize: "12px", color: "#495057", fontWeight: "500" }}>
                                                {lang.auto_create_nodes_label}
                                            </label>
                                        </div>
                                    </div>
                                    <div className="d-flex align-items-center">
                                        <div style={{ flex: 1 }}>
                                            <MyMultiSelect
                                                data={[]}
                                                name="Nodes"
                                                dataItemKey="_id"
                                                textField="Name"
                                                style={{ width: "100%" }}
                                                value={dataItem.Nodes}
                                                onChange={onChange}
                                                disabled={dataItem.AutoCreateNode}
                                            />
                                        </div>
                                        <button
                                            type="button"
                                            className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-success ms-2"
                                            style={{ width: "32px", height: "32px", display: "flex", alignItems: "center", justifyContent: "center", marginLeft: "8px" }}
                                            onClick={() => setShowNodePopup(true)}
                                            title={lang.attach_nodes_button_tooltip}
                                            disabled={dataItem?.ParentNodes?.length === 0 || dataItem.AutoCreateNode}
                                        >
                                            <i className="fa fa-ellipsis-h" aria-hidden="true"></i>
                                        </button>
                                    </div>
                                </div>
                                <div className="col-12 mt-3">
                                    <label style={{ fontWeight: "500", fontSize: "14px", color: "#495057", marginBottom: "4px" }}>
                                        {lang.platform_label} *
                                    </label>
                                    <MyMultiSelect
                                        data={ottPlatforms}
                                        name="Ott_Platforms"
                                        dataItemKey="_id"
                                        textField="Description"
                                        value={dataItem.Ott_Platforms}
                                        onChange={onChange}
                                        style={{ width: "100%" }}
                                    />
                                </div>
                                {/* Premium Date Update Section */}
                                <div className="col-12 mt-4">
                                    <div className="d-flex justify-content-between align-items-center" style={{ padding: '12px 14px', borderBottom: '1px solid #e9ecef', borderTop: '1px solid #dee2e6', backgroundColor: '#f8f9fa' }}>
                                        <div>
                                            <span style={{ fontWeight: "600", color: "#333", fontSize: "14px" }}>
                                                {selectedEpisodes.length === 1
                                                    ? (lang.update_selected_episode_title || "Update Selected Episode")
                                                    : (lang.update_selected_episodes_title || "Update Selected Episodes")}
                                            </span>
                                            <div style={{ fontSize: "11px", color: "#757575", fontWeight: "500", marginTop: "2px" }}>
                                                {selectedEpisodes.length === 1
                                                    ? (selectedEpisodes[0].Title || selectedEpisodes[0].WorkingTitle || `Episode ${selectedEpisodes[0].EpisodeNumber}`)
                                                    : `Fill fields to apply to ${selectedEpisodes.length} selected items.`}
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-success"
                                            onClick={handleBulkUpdateDates}
                                            style={{ fontWeight: "600", height: "30px", fontSize: "12px", borderRadius: "4px" }}
                                        >
                                            {lang.update_label}
                                        </button>
                                    </div>
                                    <div style={{ padding: 0 }}>
                                        {/* Box 1: Start Date - Time and Stack */}
                                        <div className="card mb-3" style={{ borderLeft: 'none', borderRight: 'none', borderRadius: '0', backgroundColor: '#ffffff', borderTop: '1px solid #e0e0e0', borderBottom: '1px solid #e0e0e0', padding: '14px' }}>
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <label style={{ fontWeight: "600", fontSize: "12px", color: "#495057", textTransform: "uppercase", margin: 0 }}>
                                                    {lang.start_date_time_label}
                                                </label>
                                                <label className="mb-0 d-flex align-items-center" style={{ fontSize: "11px", fontWeight: "600", cursor: "pointer", userSelect: "none" }}>
                                                    <input
                                                        type="checkbox"
                                                        className="me-1"
                                                        checked={updateStartDate}
                                                        onChange={(e) => {
                                                            setUpdateStartDate(e.target.checked);
                                                            if (!e.target.checked) {
                                                                setUseStartStack(false);
                                                                setStartStackOffset(0);
                                                            }
                                                        }}
                                                        style={{ cursor: "pointer", width: "13px", height: "13px", margin: 0 }}
                                                    />
                                                    <span style={{ marginLeft: "5px" }}>{lang.update_label}</span>
                                                </label>
                                            </div>
                                            <div className="d-flex" style={{ gap: "10px" }}>
                                                <div style={{ flex: 1 }}>
                                                    <input
                                                        type="date"
                                                        name="bulkStartDate"
                                                        value={bulkStartDate}
                                                        onChange={(e) => setBulkStartDate(e.target.value)}
                                                        disabled={!updateStartDate}
                                                        className="form-control form-control-sm mb-2"
                                                        style={{ borderRadius: "4px" }}
                                                    />
                                                </div>
                                                <div style={{ width: "120px" }}>
                                                    <TimeRangePicker
                                                        name="bulkStartTime"
                                                        value={bulkStartTime}
                                                        onChange={(e) => setBulkStartTime(e.target.value)}
                                                        disabled={!updateStartDate}
                                                    />
                                                </div>
                                            </div>
                                            {selectedEpisodes.length > 1 && (
                                                <div className="d-flex align-items-center justify-content-between pt-2 mt-1 border-top">
                                                    <div className="d-flex align-items-center">
                                                        <input
                                                            type="checkbox"
                                                            id="start-stack-checkbox"
                                                            className="me-2"
                                                            checked={useStartStack}
                                                            onChange={(e) => setUseStartStack(e.target.checked)}
                                                            style={{ cursor: "pointer", width: "16px", height: "16px" }}
                                                            disabled={!updateStartDate}
                                                        />
                                                        <label htmlFor="start-stack-checkbox" className="mb-0" style={{ fontWeight: "600", fontSize: "12px", color: "#495057", textTransform: "uppercase", cursor: "pointer", userSelect: "none", marginLeft: "5px" }}>
                                                            {lang.start_date_stack_label}
                                                        </label>
                                                    </div>
                                                    {useStartStack && (
                                                        <div style={{ width: "130px" }}>
                                                            <input
                                                                type="number"
                                                                className="form-control form-control-sm"
                                                                style={{ borderRadius: "4px" }}
                                                                min="0"
                                                                placeholder={lang.stack_offset_label || "Offset (Days)"}
                                                                value={startStackOffset}
                                                                onChange={(e) => setStartStackOffset(parseInt(e.target.value) || 0)}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        {/* Box 2: End Date - Time and Stack */}
                                        <div className="card mb-2" style={{ borderLeft: 'none', borderRight: 'none', borderRadius: '0', backgroundColor: '#ffffff', borderTop: '1px solid #e0e0e0', borderBottom: '1px solid #e0e0e0', padding: '14px' }}>
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <label style={{ fontWeight: "600", fontSize: "12px", color: "#495057", textTransform: "uppercase", margin: 0 }}>
                                                    {lang.end_date_time_label}
                                                </label>
                                                <label className="mb-0 d-flex align-items-center" style={{ fontSize: "11px", fontWeight: "600", cursor: "pointer", userSelect: "none" }}>
                                                    <input
                                                        type="checkbox"
                                                        className="me-1"
                                                        checked={updateEndDate}
                                                        onChange={(e) => {
                                                            setUpdateEndDate(e.target.checked);
                                                            if (!e.target.checked) {
                                                                setUseEndStack(false);
                                                                setEndStackOffset(0);
                                                            }
                                                        }}
                                                        style={{ cursor: "pointer", width: "13px", height: "13px", margin: 0 }}
                                                    />
                                                    <span style={{ marginLeft: "5px" }}>{lang.update_label}</span>
                                                </label>
                                            </div>
                                            <div className="d-flex" style={{ gap: "10px" }}>
                                                <div style={{ flex: 1 }}>
                                                    <input
                                                        type="date"
                                                        name="bulkEndDate"
                                                        value={bulkEndDate}
                                                        onChange={(e) => setBulkEndDate(e.target.value)}
                                                        disabled={!updateEndDate}
                                                        className="form-control form-control-sm mb-2"
                                                        style={{ borderRadius: "4px" }}
                                                    />
                                                </div>
                                                <div style={{ width: "120px" }}>
                                                    <TimeRangePicker
                                                        name="bulkEndTime"
                                                        value={bulkEndTime}
                                                        onChange={(e) => setBulkEndTime(e.target.value)}
                                                        disabled={!updateEndDate}
                                                    />
                                                </div>
                                            </div>
                                            {selectedEpisodes.length > 1 && (
                                                <div className="d-flex align-items-center justify-content-between pt-2 mt-1 border-top">
                                                    <div className="d-flex align-items-center">
                                                        <input
                                                            type="checkbox"
                                                            id="end-stack-checkbox"
                                                            className="me-2"
                                                            checked={useEndStack}
                                                            onChange={(e) => setUseEndStack(e.target.checked)}
                                                            style={{ cursor: "pointer", width: "16px", height: "16px" }}
                                                            disabled={!updateEndDate}
                                                        />
                                                        <label htmlFor="end-stack-checkbox" className="mb-0" style={{ fontWeight: "600", fontSize: "12px", color: "#495057", textTransform: "uppercase", cursor: "pointer", userSelect: "none", marginLeft: "5px" }}>
                                                            {lang.end_date_stack_label}
                                                        </label>
                                                    </div>
                                                    {useEndStack && (
                                                        <div style={{ width: "130px" }}>
                                                            <input
                                                                type="number"
                                                                className="form-control form-control-sm"
                                                                style={{ borderRadius: "4px" }}
                                                                min="0"
                                                                placeholder={lang.stack_offset_label || "Offset (Days)"}
                                                                value={endStackOffset}
                                                                onChange={(e) => setEndStackOffset(parseInt(e.target.value) || 0)}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {showNodePopup && (
                <BossDialog title={lang.select_nodes_dialog_header || "Select Nodes"} width="600px" onClose={() => setShowNodePopup(false)}>
                    <NodeTreeViewForm
                        selectedNodeIds={dataItem?.ParentNodes?.map(n => n?._id) ?? []}
                        isSaveButton={true}
                        isMultiSelect={false}
                        onClose={() => setShowNodePopup(false)}
                        handleSelectedNodes={handleSelectedNodes}
                        showCatalogue={false}
                        isFromMedia={true}
                    />
                </BossDialog>
            )}
        </BossDialog>
    );
};

export default CreateSeriesTransmissionForm;